(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE_CAP = 1100;
  const DESKTOP_CAP = 1920;

  function parseConfig(root) {
    const script = root.querySelector('[data-deferred-hero-config]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  function normalizeSources(slot) {
    if (!slot) return [];
    if (typeof slot === 'string') {
      return slot ? [{ url: slot, width: DESKTOP_CAP }] : [];
    }
    if (!Array.isArray(slot)) return [];
    return slot.filter((source) => source && source.url);
  }

  function hasSources(slot) {
    return normalizeSources(slot).length > 0;
  }

  function pickVideoSlot(config, isMobile) {
    const STORAGE_KEY = 'user_gender';
    let gender = '';
    try {
      gender = (localStorage.getItem(STORAGE_KEY) || '').toLowerCase();
    } catch (e) {}

    if (gender === 'male') {
      const male = isMobile
        ? (hasSources(config.mobileMale) ? config.mobileMale : config.desktopMale)
        : config.desktopMale;
      if (hasSources(male)) return male;
    }

    if (isMobile) {
      return hasSources(config.mobile) ? config.mobile : config.desktop;
    }
    return hasSources(config.desktop) ? config.desktop : config.mobile;
  }

  function pickTranscode(sources, isMobile) {
    const list = normalizeSources(sources);
    if (!list.length) return null;

    const cap = isMobile ? MOBILE_CAP : DESKTOP_CAP;
    const need = Math.min(cap, Math.ceil(window.innerWidth * (window.devicePixelRatio || 1)));
    const sorted = list.slice().sort((a, b) => (a.width || 0) - (b.width || 0));
    return sorted.find((source) => (source.width || 0) >= need) || sorted[sorted.length - 1];
  }

  function pickVideoSource(config) {
    if (!config) return null;
    const bp = Number(config.breakpoint) || 768;
    const isMobile = window.innerWidth < bp;
    return pickTranscode(pickVideoSlot(config, isMobile), isMobile);
  }

  function pickPosterUrl(config) {
    if (!config) return '';
    const bp = Number(config.breakpoint) || 768;
    const isMobile = window.innerWidth < bp;
    return (isMobile ? config.posterMobile : config.poster) || config.poster || '';
  }

  class DeferredVideoHero {
    constructor(root) {
      this.root = root;
      this.video = root.querySelector('.deferred-hero__video');
      this.config = parseConfig(root);
      this.revealed = false;
      this.loading = false;
      this.currentSrc = '';
      this.currentWidth = 0;
      this.fallbackTimer = null;
      this.resizeTimer = null;
      this.lastBreakpointSide = null;

      root.deferredVideoHero = this;

      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.onCanPlay = this.onCanPlay.bind(this);
      this.onLoadedData = this.onLoadedData.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onResize = this.onResize.bind(this);

      this.resetVideoToPoster();
      this.bindEvents();
      this.scheduleLoad();
    }

    getSource() {
      if (!this.video) return null;
      let source = this.video.querySelector('source[data-deferred-hero-source]');
      if (!source) {
        source = document.createElement('source');
        source.setAttribute('data-deferred-hero-source', '');
        source.setAttribute('type', 'video/mp4');
        this.video.appendChild(source);
      }
      return source;
    }

    clearVideoSource() {
      if (!this.video) return;
      const source = this.getSource();
      if (source) source.removeAttribute('src');
      this.video.removeAttribute('src');
      this.video.load();
    }

    prefersReducedMotion() {
      return REDUCED_MOTION.matches;
    }

    bindEvents() {
      window.addEventListener('resize', this.onResize);

      if (this.video) {
        this.video.addEventListener('canplay', this.onCanPlay);
        this.video.addEventListener('loadeddata', this.onLoadedData);
        this.video.addEventListener('transitionend', this.onTransitionEnd);
      }
    }

    scheduleLoad() {
      if (this.prefersReducedMotion()) return;

      if (document.readyState === 'complete') {
        this.loadVideo();
      } else {
        window.addEventListener('load', this.onWindowLoad, { once: true });
      }
    }

    onWindowLoad() {
      this.loadVideo();
    }

    /* Reset video layer only — LCP <picture> poster stays in the DOM untouched. */
    resetVideoToPoster() {
      this.revealed = false;
      this.loading = false;
      this.clearFallback();

      if (this.video) {
        this.video.pause();
        this.clearVideoSource();

        const poster = pickPosterUrl(this.config);
        if (poster) {
          this.video.setAttribute('poster', poster);
        } else {
          this.video.removeAttribute('poster');
        }
      }

      this.root.classList.remove('deferred-hero--video-ready');
      this.currentSrc = '';
      this.currentWidth = 0;
    }

    loadVideo() {
      if (this.prefersReducedMotion()) return;
      if (!this.video || !this.config) return;

      const picked = pickVideoSource(this.config);
      if (!picked || !picked.url) return;

      const url = picked.url;

      this.loading = true;
      this.revealed = false;
      this.clearFallback();
      this.root.classList.remove('deferred-hero--video-ready');

      if (this.currentSrc !== url) {
        this.currentSrc = url;
        this.currentWidth = picked.width || 0;
        const source = this.getSource();
        if (source) source.src = url;
        this.video.removeAttribute('src');
      }

      this.video.load();
      this.lastBreakpointSide = window.innerWidth < (Number(this.config.breakpoint) || 768) ? 'mobile' : 'desktop';

      this.fallbackTimer = window.setTimeout(() => {
        if (this.loading && !this.revealed) this.revealVideo();
      }, 10000);
    }

    onCanPlay() {
      if (this.loading && !this.revealed) this.revealVideo();
    }

    onLoadedData() {
      if (this.loading && !this.revealed) this.revealVideo();
    }

    revealVideo() {
      if (this.revealed || !this.video) return;

      this.revealed = true;
      this.loading = false;
      this.clearFallback();

      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }

      this.root.classList.add('deferred-hero--video-ready');
    }

    onTransitionEnd(event) {
      if (event.target !== this.video || event.propertyName !== 'opacity') return;
      if (!this.root.classList.contains('deferred-hero--video-ready')) return;

      /* Drop redundant video poster attr; visible <picture> remains underneath. */
      this.video.removeAttribute('poster');
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.handleResize(), 150);
    }

    handleResize() {
      if (this.prefersReducedMotion()) return;
      if (!this.config) return;

      const bp = Number(this.config.breakpoint) || 768;
      const side = window.innerWidth < bp ? 'mobile' : 'desktop';
      const next = pickVideoSource(this.config);

      if (!next || !next.url) return;

      const slotChanged = side !== this.lastBreakpointSide;
      if (!slotChanged && this.currentSrc) {
        if (next.url === this.currentSrc) return;
        if (this.currentWidth && next.width <= this.currentWidth) return;
      }

      this.lastBreakpointSide = side;
      this.resetVideoToPoster();
      this.loadVideo();
    }

    clearFallback() {
      if (this.fallbackTimer) {
        window.clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
    }

    destroy() {
      this.clearFallback();
      window.clearTimeout(this.resizeTimer);
      window.removeEventListener('load', this.onWindowLoad);
      window.removeEventListener('resize', this.onResize);

      if (this.video) {
        this.video.pause();
        this.video.removeEventListener('canplay', this.onCanPlay);
        this.video.removeEventListener('loadeddata', this.onLoadedData);
        this.video.removeEventListener('transitionend', this.onTransitionEnd);
        this.clearVideoSource();
      }

      this.root.classList.remove('deferred-hero--video-ready');
      this.root.removeAttribute('data-deferred-hero-init');
      delete this.root.deferredVideoHero;
    }
  }

  function initDeferredVideoHeroes(root = document) {
    root.querySelectorAll('[data-deferred-hero]:not([data-deferred-hero-init])').forEach((el) => {
      el.setAttribute('data-deferred-hero-init', '');
      new DeferredVideoHero(el);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initDeferredVideoHeroes());

  document.addEventListener('shopify:section:load', (event) => {
    initDeferredVideoHeroes(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-deferred-hero]').forEach((el) => {
      el.deferredVideoHero?.destroy();
    });
  });
})();
